import * as queryString from 'query-string';
import * as React from 'react';
import { useState } from 'react';
import { Messages } from '~src/codegen/Localize';
import {
    GetDataConnectionAuthInfoRequest,
    IGetDataConnectionAuthInfoParams,
} from '../../ts/ServerApi/requests/GetDataConnectionAuthInfoRequest';
import { IDataConnectionAuthInfo } from '../../ts/ServerApi/types/IDataConnectionAuthInfo';
import { WindowPopupService } from '../../authentication/services/WindowPopupService';
import { Location } from '~src/common/react/Location';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { AuthenticateDatabase } from '../resources/AuthenticateDatabase';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { AuthenticationComplete, AuthenticationIncomplete } from './AuthenticateConnectionDisplayComponents';

export interface IAuthenticateDatabaseParams {
    readonly authentication: string;
    readonly username: string;
    readonly password?: string;
    readonly reuse_forever?: boolean;
    readonly reuse?: boolean;
    readonly crypted: string;
    readonly connection: string;
    readonly path: string;
    readonly authenticity_token?: string;
    readonly oauthrequest?: boolean;
}

export interface IConnectionProperty {
    readonly message?: string;
    readonly value?: string;
    readonly tbTestId?: string;
}
export interface ConnectionAuthenticationProps {
    readonly queryParams: ConnectionAuthenticationQueryParams;
    readonly authInfo: IDataConnectionAuthInfo;
}
export interface ConnectionAuthenticationQueryParams extends IGetDataConnectionAuthInfoParams {
    readonly isPreview: string;
}


export function AuthenticateConnection(props: ConnectionAuthenticationProps) {

    const usernameRef = React.useRef<HTMLInputElement>(null);
    const passwordRef = React.useRef<HTMLInputElement>(null);

    function blurPassword() {
        passwordRef.current.blur();
    }

    // Updated in the authentication flow
    const [authInfo, setAuthInfo] = useState<IDataConnectionAuthInfo>(props.authInfo);
    const [errorMessageKey, setErrorMessageKey] = useState<string>('');
    const [busy, setBusy] = useState<boolean>(false);
    const [doneWithWindow, setDoneWithWindow] = useState<boolean>(false);

    // Updated by the user
    const [selectedConnectionAuthMode, setSelectedConnectionAuthMode] = useState<string>(props.authInfo.authenticationMode);
    const [authenticationUsername, setAuthenticationUsername] = useState<string>(props.authInfo.defaultUsername);
    const [authenticationPassword, setAuthenticationPassword] = useState<string>('');
    const [authenticationRememberPassword, setAuthenticationRememberPassword] = useState<boolean>(props.authInfo.rememberPasswordSelectedForDs);

    function attemptAuth() {
        setBusy(true);
        sendDataSourceCredentialsToServer({
            queryParams: props.queryParams,
            authInfo,
            authenticationRememberPassword,
            selectedConnectionAuthMode,
            authenticationUsername,
            authenticationPassword,
            setDoneWithWindow,
            setBusy,
            setAuthInfo,
            setErrorMessageKey,
            blurPassword: blurPassword,
            retrying: false
        });
    }

    if (doneWithWindow) {
        return <AuthenticationComplete />;
    } else {
        return <AuthenticationIncomplete
            connectionAuthenticationProps={props}
            attemptAuth={attemptAuth}
            busy={busy}
            authenticationRememberPassword={authenticationRememberPassword}
            errorMessageKey={errorMessageKey}
            selectedConnectionAuthMode={selectedConnectionAuthMode}
            authenticationUsername={authenticationUsername}
            authenticationPassword={authenticationPassword}
            setSelectedConnectionAuthMode={setSelectedConnectionAuthMode}
            setAuthenticationUsername={setAuthenticationUsername}
            setAuthenticationPassword={setAuthenticationPassword}
            setAuthenticationRememberPassword={setAuthenticationRememberPassword}
            usernameRef={usernameRef}
            passwordRef={passwordRef}
        />;
    }
}

async function sendDataSourceCredentialsToServer({
    queryParams,
    authInfo,
    authenticationRememberPassword,
    selectedConnectionAuthMode,
    authenticationUsername,
    authenticationPassword,
    setDoneWithWindow,
    setBusy,
    setAuthInfo,
    setErrorMessageKey,
    blurPassword: blurPassword,
    retrying,
}: {
    queryParams: ConnectionAuthenticationQueryParams;
    authInfo: IDataConnectionAuthInfo;
    authenticationRememberPassword: boolean;
    selectedConnectionAuthMode: string;
    authenticationUsername: string;
    authenticationPassword: string;
    setDoneWithWindow: React.Dispatch<React.SetStateAction<boolean>>;
    setBusy: React.Dispatch<React.SetStateAction<boolean>>;
    setAuthInfo: React.Dispatch<React.SetStateAction<IDataConnectionAuthInfo>>;
    setErrorMessageKey: React.Dispatch<React.SetStateAction<string>>;
    blurPassword: () => void;
    retrying: boolean;
}): Promise<void> {

    // TODO Figure out the correct response from the authenticate method
    const authenticateParams = AuthenticateDatabase.getAuthenticateDatabaseParams(
        queryParams,
        authInfo,
        authenticationRememberPassword,
        selectedConnectionAuthMode,
        authenticationUsername,
        authenticationPassword
    );
    try {
        await AuthenticateDatabase.authenticate(authInfo.actionPath, authenticateParams);
        // Success
        // Force the password input to lose focus before navigating, to work around an iOS Safari issue.
        // For more info, see TFS 564502: Mobile: Cursor isn't dismissed after ...
        blurPassword();

        // Authoring sessions are not opened in a popup window and need to remain open after authenticating
        // But When feature SkipConnectionsDuringLoad is set, then we should just close popup
        // When SkipConnectionsDuringLoad is enabled, URL will have vizqlSessionId param
        const vizqlSessionId = queryString.parse(Location.instance.search())['vizqlSessionId'];
        const skipConnectionsDuringLoadEnabled = (vizqlSessionId !== undefined);
        const authenticatingForBootstrap = (vizqlSessionId === '0');
        if ((authInfo.isAuthoringSession && !skipConnectionsDuringLoadEnabled) || !WindowPopupService.maybeNotifyAndClose()) {
            // We can embed auth dialog in case when whole hierarchy of documents belongs to Tableau Server
            // In that case we don't want to reload the page, we want to signal that auth completed.
            // So, if we are here, it means that current page is not popup.
            if (skipConnectionsDuringLoadEnabled && !authenticatingForBootstrap) {
                // Just signal that auth completed.
                AuthenticateDatabase.signalAuthCompleted();
            } else {
                // If vizqlSessionId is 0, it means it is a bootstrap and we redirect.
                AuthenticateDatabase.navigateTo(queryParams.path);
            }
        }
        // toggle a state that shows a 'close me' message (tfsid 389619)
        // Toggle a state only when this page is opened in popup (tfsid 464123)
        if (AuthenticateDatabase.opener) {
            setDoneWithWindow(true);
        }
        setBusy(false);
    } catch (error) {
        // Error handler
        if (error && error.response && error.response.data && error.response.data.shouldRetry) {
            // Defect 513697:Authentication fails if you open two different "prompt user" sheets in different browser tabs
            if (!retrying) {
                // Preventing infinite loop. Only one retry allowed.
                const result: IDataConnectionAuthInfo = await WebHttpClient.sendRequest(new GetDataConnectionAuthInfoRequest(queryParams));
                // modulus & exponent might be stale, we just want to retry & not bother user.
                setAuthInfo(result);
                sendDataSourceCredentialsToServer({
                    queryParams,
                    authInfo: result,
                    authenticationRememberPassword,
                    selectedConnectionAuthMode,
                    authenticationUsername,
                    authenticationPassword,
                    setDoneWithWindow,
                    setBusy,
                    setAuthInfo,
                    setErrorMessageKey,
                    blurPassword,
                    retrying: true
                });
                return;
            }
        }

        // TODO: better error reporting once we figure out what kind of errors to expect
        setErrorMessageKey(Messages.errorUnknown());
        setBusy(false);
    }
}
